import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { routes } from '@/app/routes'
import { ApiError } from '@/lib/api'
import { queryClient } from '@/lib/query-client'
import { useSessionStore } from '@/stores/session.store'
import { authApi, type CompleteProfileBody, type OtpAlternative, type OtpChannel, type OtpDelivery } from '@/features/auth/auth.api'
import { formatMobile, maskMobile } from '@/features/auth/mobile-validator'
import { authDestination, destinationPath } from '@/features/auth/auth-destination'
import { clearHome, loadHome } from '@/features/auth/useBoot'
import { clearRef, readRef } from '@/features/referral/ref-capture'

/** What the login page hands to the verify page (router state, never the URL). */
export interface VerifyState {
  otpToken: string
  /** "+91 98 ****** 40" for the subtitle. */
  maskedMobile: string
  cc: string
  number: string
  /** Where the first code went (LP-1036); absent means SMS. */
  channel?: OtpChannel
  /** What the person may ask for instead; absent or empty offers nothing new. */
  alternatives?: OtpAlternative[]
}

export const messageOf = (e: unknown, fallback = 'Something went wrong') => (e instanceof ApiError ? e.message : fallback)

export function useSendOtp() {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const send = useCallback(
    async (cc: string, number: string, countryCode?: string) => {
      setLoading(true)
      setError(null)
      try {
        // A fresh sign-in never reuses a stale pair from this browser.
        useSessionStore.getState().signOut()
        const { token, channel, alternatives } = await authApi.sendOtp(formatMobile(cc, number), countryCode)
        const state: VerifyState = { otpToken: token, maskedMobile: maskMobile(cc, number), cc, number, channel, alternatives }
        navigate({ pathname: routes.verify, search: location.search }, { state, replace: true })
      } catch (e) {
        setError(messageOf(e))
      } finally {
        setLoading(false)
      }
    },
    [navigate, location.search],
  )
  return { send, loading, error, clearError: () => setError(null) }
}

/** Verify the code, store the session; RequireGuest then routes by the destination rule. */
export function useVerifyOtp() {
  const [loading, setLoading] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [resendError, setResendError] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  const [resendRound, setResendRound] = useState(0)
  const [expired, setExpired] = useState(false)

  const verify = useCallback(async (otpToken: string, code: string) => {
    setLoading(true)
    setCodeError(null)
    try {
      const { accessToken, refreshToken, user } = await authApi.verifyOtp(otpToken, code, readRef())
      // Sent once: a later sign-in in this tab is not a new referral.
      clearRef()
      queryClient.clear()
      useSessionStore.getState().setTokens({ accessToken, refreshToken })
      if (user.profileCompleted) await loadHome(user)
      useSessionStore.getState().setUser(user)
      // Loading stays on: the guard is about to move us.
    } catch (e) {
      setLoading(false)
      if (e instanceof ApiError && e.status === 401) setExpired(true)
      else setCodeError(messageOf(e))
      throw e
    }
  }, [])

  /** Resends, on `channel` when the person picked one; returns where the new code went, or null on failure. */
  const resend = useCallback(async (otpToken: string, channel?: OtpChannel): Promise<OtpDelivery | null> => {
    setResending(true)
    setResendError(null)
    try {
      const delivery = await authApi.resendOtp(otpToken, channel)
      setResendRound((r) => r + 1)
      setCodeError(null)
      return delivery
    } catch (e) {
      setResendError(messageOf(e))
      return null
    } finally {
      setResending(false)
    }
  }, [])

  return { verify, resend, loading, resending, codeError, resendError, resendRound, expired, clearErrors: () => { setCodeError(null); setResendError(null) } }
}

/** Finish onboarding: swap only the access token, set the user; RequireIncompleteProfile routes home. */
export function useCompleteProfile() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const complete = useCallback(async (body: CompleteProfileBody) => {
    setLoading(true)
    setError(null)
    try {
      const { accessToken, user } = await authApi.completeProfile(body)
      const { refreshToken } = useSessionStore.getState()
      useSessionStore.getState().setTokens({ accessToken, refreshToken: refreshToken ?? '' })
      await loadHome(user)
      useSessionStore.getState().setUser(user)
      return destinationPath(authDestination(user))
    } catch (e) {
      setError(messageOf(e))
      setLoading(false)
      return null
    }
  }, [])
  return { complete, loading, error, clearError: () => setError(null) }
}

/** Sign out everywhere this browser is concerned: revoke the pair server-side, clear caches, go to login. */
export async function signOutEverywhere(navigate: (path: string) => void) {
  const { refreshToken, signOut } = useSessionStore.getState()
  try {
    await authApi.logout(refreshToken)
  } catch {
    // The server session may already be gone; the local one goes regardless.
  } finally {
    queryClient.clear()
    clearHome()
    signOut()
    // Let RequireAuth's own redirect (which would carry ?next=) settle first, then land on a clean /login.
    await new Promise((r) => setTimeout(r, 0))
    navigate(routes.login)
  }
}
